import { css } from "@emotion/react";
import { Theme, useTheme } from "@mui/material";
import Link from "next/link";

import { totalPoint } from "~/components/me/bento/content/point-report/lib";
import {
  fixedHeight,
  fixedWidth,
  flexRow,
  mediaQueryIsDesktop,
  mediaQueryIsMobile,
} from "~/lib/client/design";
import { SP_MODE_WIDTH } from "~/lib/client/hooks/useIsSpMode";

type Props = {
  point: {
    category: {
      id: number;
      name: string;
    };
    creatorPoint: number;
    curatorPoint: number;
    evangelistPoint: number;
    expertPoint: number;
    supporterPoint: number;
  };
  ordinalLabel: string;
};

const CategoryPointPanel = ({ point, ordinalLabel }: Props) => {
  const theme = useTheme();

  return (
    <Link css={rootStyle} href={`/category/${point.category.id}`}>
      <div css={ordinalAndPointStyle}>
        <span css={ordinalLabelStyle(theme)}>{ordinalLabel}</span>
        <span css={pointStyle}>{totalPoint(point).toLocaleString()}pt</span>
      </div>
      <hr color={theme.palette.divider} />
      <span css={categoryNameStyle}>{point.category.name}</span>
      <div css={pointLabelContainerStyle}>
        <div css={pointLabelStyle}>
          <span>クリエイター</span>
          <span>{point.creatorPoint.toLocaleString()}pt</span>
        </div>
        <div css={pointLabelStyle}>
          <span>キュレーター</span>
          <span>{point.curatorPoint.toLocaleString()}pt</span>
        </div>
        <div css={pointLabelStyle}>
          <span>エバンジェリスト</span>
          <span>{point.evangelistPoint.toLocaleString()}pt</span>
        </div>
        <div css={pointLabelStyle}>
          <span>エキスパート</span>
          <span>{point.expertPoint.toLocaleString()}pt</span>
        </div>
        <div css={pointLabelStyle}>
          <span>サポーター</span>
          <span>{point.supporterPoint.toLocaleString()}pt</span>
        </div>
      </div>
    </Link>
  );
};

const rootStyle = css`
  background: white;
  border: 1px solid #fff;
  backdrop-filter: blur(20px);

  hr {
    margin: 0;
  }

  ${mediaQueryIsDesktop(css`
    ${fixedWidth("230px")};
    ${fixedHeight("230px")};
    border-radius: 20px;
  `)};

  ${mediaQueryIsMobile(css`
    ${fixedWidth("160px")};
    ${fixedHeight("160px")};
    border-radius: 1rem;
  `)};
`;

const ordinalAndPointStyle = css`
  ${flexRow};
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem 0.25rem 1rem;
  max-width: 160px;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    span {
      font-size: 14px;
    }
  }
`;

const ordinalLabelStyle = (theme: Theme) => css`
  font-size: 19px;
  font-weight: 600;
  color: ${theme.palette.primary.main};
`;

const pointStyle = css`
  font-size: 26px;
  font-weight: 600;
`;

const categoryNameStyle = css`
  font-weight: 600;
  display: block;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${mediaQueryIsMobile(css`
    font-size: 14px;
    margin: 0.25rem;
  `)};

  ${mediaQueryIsDesktop(css`
    font-size: 16px;
    margin: 1rem;
  `)};
`;

const pointLabelContainerStyle = css`
  margin: 0.5rem 1rem;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    margin: 0.25rem;
  }
`;

const pointLabelStyle = css`
  ${fixedWidth("10.5rem")};
  ${flexRow};
  justify-content: space-between;
  font-size: 14px;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    ${fixedWidth("140px")};
    font-size: 12px;
  }
`;

export default CategoryPointPanel;
